import * as React from 'react';

interface WidgetScopeProps {
  children: React.ReactNode;
  className?: string;
}

const WidgetScope: React.FC<WidgetScopeProps> = ({ children, className = '' }) => {
  const [stylesInjected, setStylesInjected] = React.useState(false);

  React.useEffect(() => {
    if (stylesInjected) return;

    // Check if styles are already injected
    const existingStyles = document.getElementById('widget-styles');
    if (existingStyles) {
      setStylesInjected(true);
      return;
    }

    // Create and inject comprehensive CSS
    const style = document.createElement('style');
    style.id = 'widget-styles';
    style.textContent = `
      /* CSS Variables for widget- prefixed components */
      .widget-scope {
        --widget-background: 0 0% 100%;
        --widget-foreground: 222.2 84% 4.9%;
        --widget-card: 0 0% 100%;
        --widget-card-foreground: 222.2 84% 4.9%;
        --widget-popover: 0 0% 100%;
        --widget-popover-foreground: 222.2 84% 4.9%;
        --widget-primary: 222.2 47.4% 11.2%;
        --widget-primary-foreground: 210 40% 98%;
        --widget-secondary: 210 40% 96%;
        --widget-secondary-foreground: 222.2 84% 4.9%;
        --widget-muted: 210 40% 96%;
        --widget-muted-foreground: 215.4 16.3% 46.9%;
        --widget-accent: 210 40% 96%;
        --widget-accent-foreground: 222.2 84% 4.9%;
        --widget-destructive: 0 84.2% 60.2%;
        --widget-destructive-foreground: 210 40% 98%;
        --widget-border: 214.3 31.8% 91.4%;
        --widget-input: 214.3 31.8% 91.4%;
        --widget-ring: 222.2 84% 4.9%;
        --widget-radius: 0.5rem;
      }

      /* Dark mode support */
      @media (prefers-color-scheme: dark) {
        .widget-scope {
          --widget-background: 222.2 84% 4.9%;
          --widget-foreground: 210 40% 98%;
          --widget-card: 222.2 84% 4.9%;
          --widget-card-foreground: 210 40% 98%;
          --widget-popover: 222.2 84% 4.9%;
          --widget-popover-foreground: 210 40% 98%;
          --widget-primary: 210 40% 98%;
          --widget-primary-foreground: 222.2 47.4% 11.2%;
          --widget-secondary: 217.2 32.6% 17.5%;
          --widget-secondary-foreground: 210 40% 98%;
          --widget-muted: 217.2 32.6% 17.5%;
          --widget-muted-foreground: 215 20.2% 65.1%;
          --widget-accent: 217.2 32.6% 17.5%;
          --widget-accent-foreground: 210 40% 98%;
          --widget-destructive: 0 62.8% 30.6%;
          --widget-destructive-foreground: 210 40% 98%;
          --widget-border: 217.2 32.6% 17.5%;
          --widget-input: 217.2 32.6% 17.5%;
          --widget-ring: 212.7 26.8% 83.9%;
        }
      }

      /* Layout utilities */
      .widget-inline-flex { display: inline-flex; }
      .widget-flex { display: flex; }
      .widget-flex-col { flex-direction: column; }
      .widget-items-center { align-items: center; }
      .widget-justify-center { justify-content: center; }
      .widget-space-y-1\\.5 > * + * { margin-top: 0.375rem; }
      .widget-space-y-2 > * + * { margin-top: 0.5rem; }
      .widget-space-y-4 > * + * { margin-top: 1rem; }
      .widget-whitespace-nowrap { white-space: nowrap; }
      .widget-relative { position: relative; }
      .widget-absolute { position: absolute; }
      .widget-overflow-hidden { overflow: hidden; }
      .widget-min-w-0 { min-width: 0px; }
      .widget-shrink-0 { flex-shrink: 0; }
      .widget-grow-0 { flex-grow: 0; }
      .widget-basis-full { flex-basis: 100%; }
      .widget-max-w-md { max-width: 28rem; }
      .widget-mx-auto { margin-left: auto; margin-right: auto; }
      .widget-w-full { width: 100%; }
      .widget-h-8 { height: 2rem; }
      .widget-h-10 { height: 2.5rem; }
      .widget-w-8 { width: 2rem; }
      .widget-w-10 { width: 2.5rem; }

      /* Spacing utilities */
      .widget-p-1 { padding: 0.25rem; }
      .widget-p-2 { padding: 0.5rem; }
      .widget-p-4 { padding: 1rem; }
      .widget-p-6 { padding: 1.5rem; }
      .widget-px-4 { padding-left: 1rem; padding-right: 1rem; }
      .widget-py-2 { padding-top: 0.5rem; padding-bottom: 0.5rem; }
      .widget-pt-0 { padding-top: 0px; }
      .widget-pl-4 { padding-left: 1rem; }
      .widget--ml-4 { margin-left: -1rem; }
      .widget--left-12 { left: -3rem; }
      .widget--right-12 { right: -3rem; }
      .widget-top-1\\/2 { top: 50%; }
      .widget--translate-y-1\\/2 { transform: translateY(-50%); }

      /* Typography utilities */
      .widget-text-sm { font-size: 0.875rem; line-height: 1.25rem; }
      .widget-text-2xl { font-size: 1.5rem; line-height: 2rem; }
      .widget-font-medium { font-weight: 500; }
      .widget-font-semibold { font-weight: 600; }
      .widget-leading-none { line-height: 1; }
      .widget-tracking-tight { letter-spacing: -0.025em; }

      /* Border utilities */
      .widget-border { border-width: 1px; }
      .widget-rounded-lg { border-radius: 0.5rem; }
      .widget-rounded-full { border-radius: 9999px; }

      /* Background utilities */
      .widget-bg-background { background-color: hsl(var(--widget-background)); }
      .widget-bg-card { background-color: hsl(var(--widget-card)); }
      .widget-bg-secondary { background-color: hsl(var(--widget-secondary)); }
      .widget-bg-gradient-to-r { background-image: linear-gradient(to right, var(--tw-gradient-stops)); }
      .widget-from-\\[\\#f36744\\] { --tw-gradient-from: #f36744; --tw-gradient-to: rgb(243 103 68 / 0); --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to); }
      .widget-to-\\[\\#8e57fb\\] { --tw-gradient-to: #8e57fb; }

      /* Text color utilities */
      .widget-text-card-foreground { color: hsl(var(--widget-card-foreground)); }
      .widget-text-secondary-foreground { color: hsl(var(--widget-secondary-foreground)); }
      .widget-text-muted-foreground { color: hsl(var(--widget-muted-foreground)); }
      .widget-text-white { color: rgb(255 255 255); }

      /* Border color utilities */
      .widget-border-input { border-color: hsl(var(--widget-input)); }

      /* Shadow utilities */
      .widget-shadow-sm { box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05); }

      /* Interactive utilities */
      .widget-cursor-pointer { cursor: pointer; }
      .widget-transition-colors { transition-property: color, background-color, border-color, text-decoration-color, fill, stroke; transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1); transition-duration: 150ms; }
      .widget-ring-offset-background { --tw-ring-offset-color: hsl(var(--widget-background)); }
      .widget-focus-visible\\:widget-outline-none:focus-visible { outline: 2px solid transparent; outline-offset: 2px; }
      .widget-focus-visible\\:widget-ring-2:focus-visible { --tw-ring-offset-shadow: var(--tw-ring-inset) 0 0 0 var(--tw-ring-offset-width) var(--tw-ring-offset-color); --tw-ring-shadow: var(--tw-ring-inset) 0 0 0 calc(2px + var(--tw-ring-offset-width)) var(--tw-ring-color); box-shadow: var(--tw-ring-offset-shadow), var(--tw-ring-shadow), var(--tw-shadow, 0 0 #0000); }
      .widget-focus-visible\\:widget-ring-ring:focus-visible { --tw-ring-color: hsl(var(--widget-ring)); }
      .widget-focus-visible\\:widget-ring-offset-2:focus-visible { --tw-ring-offset-width: 2px; }
      .widget-disabled\\:widget-pointer-events-none:disabled { pointer-events: none; }
      .widget-disabled\\:widget-opacity-50:disabled { opacity: 0.5; }

      /* Hover utilities */
      .hover\\:widget-bg-accent:hover { background-color: hsl(var(--widget-accent)); }
      .hover\\:widget-text-accent-foreground:hover { color: hsl(var(--widget-accent-foreground)); }
      .hover\\:widget-bg-secondary\\/80:hover { background-color: hsl(var(--widget-secondary) / 0.8); }
      .hover\\:widget-from-\\[\\#e55a3a\\]:hover { --tw-gradient-from: #e55a3a; --tw-gradient-to: rgb(229 90 58 / 0); --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to); }
      .hover\\:widget-to-\\[\\#7c4dd8\\]:hover { --tw-gradient-to: #7c4dd8; }

      /* Transform utilities */
      .widget-rotate-90 { transform: rotate(90deg); }

      /* Accessibility */
      .widget-sr-only { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border: 0; }
    `;

    document.head.appendChild(style);
    setStylesInjected(true);

    // Cleanup function
    return () => {
      if (style.parentNode) {
        style.parentNode.removeChild(style);
      }
    };
  }, [stylesInjected]);

  return (
    <div className={`widget-scope ${className}`}>
      {children}
    </div>
  );
};

export default WidgetScope; 